// ModalShell — the ONE correct way to render a modal/dialog site-wide.
//
// WHY THIS EXISTS (owner-reported, recurring): in-place `position:fixed` modals
// were appearing cut off at the top or bottom and could not scroll. Root cause:
// a `position:fixed` element is positioned against the nearest ancestor that has
// a `transform` / `filter` / `perspective` / `will-change` / `contain` — and the
// app has several (the page reveal animation leaves a transform on its wrapper,
// the 3D showcase uses `perspective`, blur panels use `filter`). So a modal
// rendered inline anchored to that wrapper instead of the viewport → off-screen +
// unscrollable. CrudDialog already dodged this with a portal; ModalShell makes
// that pattern reusable for every other dialog.
//
// It guarantees, on every screen size (down to 375px):
//   • renders via a PORTAL to <body> → always anchored to the viewport;
//   • the backdrop scrolls (overflow-y:auto) AND the card has its own max-height +
//     overflow, so a tall dialog is fully reachable, never clipped;
//   • centered when it fits, top-aligned + scrollable when it doesn't;
//   • click-backdrop / Escape to close; background scroll locked while open;
//   • a11y: role="dialog" aria-modal, a real FOCUS TRAP (focus moves into the
//     dialog on open, Tab / Shift+Tab cycle within the dialog's focusables,
//     Esc closes, and focus is RESTORED to the element that opened the modal on
//     close). RTL-correct: Tab order follows DOM order regardless of direction.

import React from "react";
import { createPortal } from "react-dom";

// Selector for the elements that can receive keyboard focus inside the dialog.
// `[tabindex]:not([tabindex="-1"])` includes programmatically-focusable items
// but excludes the card itself (tabIndex=-1) and other -1 sinks.
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
  'select:not([disabled]), textarea:not([disabled]), iframe, object, embed, ' +
  'summary, audio[controls], video[controls], [contenteditable]:not([contenteditable="false"]), ' +
  '[tabindex]:not([tabindex="-1"])';

/** Visible, enabled focusables in DOM order within `root`. */
const focusablesIn = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      // Skip hidden / collapsed nodes — offsetParent is null for display:none,
      // and a zero client-rect catches visibility:hidden inside the card.
      el.offsetParent !== null ||
      el.getClientRects().length > 0,
  );

export interface ModalShellProps {
  /** Called on backdrop click + Escape. Omit for a non-dismissable modal. */
  onClose?: () => void;
  /** Max dialog width in px (clamped to 96vw). Default 560. */
  maxWidth?: number;
  /** id of the element that labels the dialog (for aria-labelledby). */
  labelledBy?: string;
  /** Extra style merged onto the dialog card (e.g. padding). */
  cardStyle?: React.CSSProperties;
  /** Extra className on the dialog card (so existing card visuals carry over). */
  cardClassName?: string;
  children: React.ReactNode;
}

const PAD = "clamp(12px, 4vh, 48px)";

export const ModalShell: React.FC<ModalShellProps> = ({
  onClose,
  maxWidth = 560,
  labelledBy,
  cardStyle,
  cardClassName,
  children,
}) => {
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  // Keep the latest onClose without re-running the focus-trap effect on every
  // render (which would re-grab focus + re-save the opener). The trap effect
  // mounts ONCE; Esc reads the current handler through this ref.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Lock background scroll while the modal is open.
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // FOCUS TRAP — runs once for the lifetime of the open modal.
  //   • remember the element that had focus before the modal opened;
  //   • move focus into the dialog (first focusable, else the card);
  //   • Tab / Shift+Tab cycle within the dialog's focusables (wrap-around);
  //   • Esc calls onClose (if dismissable);
  //   • on close, restore focus to the opener.
  React.useEffect(() => {
    const card = cardRef.current;
    if (!card) return undefined;

    const opener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Initial focus: first focusable inside, otherwise the card itself
    // (tabIndex=-1) so keyboard users start inside the dialog, never behind it.
    const initial = focusablesIn(card)[0] ?? card;
    initial.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        if (onCloseRef.current) {
          e.preventDefault();
          onCloseRef.current();
        }
        return;
      }
      if (e.key !== "Tab") return;

      const items = focusablesIn(card);
      if (items.length === 0) {
        // Nothing focusable inside → keep focus pinned to the card.
        e.preventDefault();
        card.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      // Wrap-around at the edges, and pull focus back in if it has somehow
      // escaped the dialog (e.g. focus was on the card or outside).
      if (e.shiftKey) {
        if (active === first || active === card || !card.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || active === card || !card.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    // Capture phase so we intercept Tab before it reaches focusables.
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      // Restore focus to the opener if it's still in the document.
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(e) => {
        // Close only on a genuine backdrop click (not a drag ending on the card).
        if (e.target === e.currentTarget && onClose) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(2,6,23,0.6)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        overscrollBehavior: "contain",
        padding: `${PAD} 16px`,
        animation: "backdrop-in 160ms ease both",
      }}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        className={cardClassName}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: `min(${maxWidth}px, 96vw)`,
          // Never taller than the viewport minus the backdrop padding → fully
          // reachable; the card scrolls internally if its content overflows.
          maxHeight: `calc(100dvh - 2 * ${PAD})`,
          overflowY: "auto",
          margin: "auto", // centers when short; top-aligns + scrolls when tall
          outline: "none",
          ...(cardClassName
            ? {}
            : {
                background: "var(--surface)",
                border: "1px solid var(--line-2)",
                borderRadius: 16,
                boxShadow: "0 24px 64px -16px rgba(0,0,0,0.6)",
              }),
          ...cardStyle,
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default ModalShell;
